import React, { useState, useRef } from "react";
import {
  Box,
  IconButton,
  Typography,
  useTheme,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Menu,
  useMediaQuery,
} from "@mui/material";
import {
  ArrowBackIosRounded,
  InfoRounded,
  BorderColorRounded,
  DeleteRounded,
  MoreVertRounded,
  DownloadRounded,
} from "@mui/icons-material";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import EditFileDialog from "../Data/DataExplorer/FileExplorer/EditFileDialog";
import FileDetailsDialog from "./FileDetailsDialog";
import { usePostHog } from "posthog-js/react";
import {
  CLICK_ACTION_MENU_ON_VIEW_FILE,
  VIEW_FILE_INFO,
  DOWNLOAD_FILE,
} from "../../utils/posthogEvents";
import Toolbar from "../../components/PDFView/Toolbar";


function Header({
  file,
  handleDeleteFile,
  handleUpdateFile,
  handleGoBack,
  zoomPluginInstance,
  pageNavigationPluginInstance,
}) {
  const posthog = usePostHog();
  const theme = useTheme();
  const isXS = useMediaQuery(theme.breakpoints.down("sm"));
  const { name } = file;

  const handleDownloadFile = async () => {
    try {
      const response = await fetch(file.file_url);
      if (!response.ok) return;
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = file.name || "file";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
      posthog.capture(DOWNLOAD_FILE);
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <Box
      px={{ xs: 2, sm: 3 }}
      py={2}
      display={"flex"}
      alignItems={"center"}
      sx={{
        background: "#323639",
        boxShadow:
          "0px 1px 10px 0px rgba(0, 0, 0, 0.12), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 2px 4px 0px rgba(0, 0, 0, 0.20)",
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
      }}
      width={"100%"}>
      <Box
        display={"flex"}
        alignItems={"center"}
        width={"calc(100% - 40px)"}
        mr={1}>
        <IconButton onClick={handleGoBack} sx={{ mr: 1, color: "white" }}>
          <ArrowBackIosRounded fontSize="small" />
        </IconButton>
        <Typography variant="h6" noWrap sx={{ color: "white" }}>
          {name}
        </Typography>
      </Box>
      {!isXS && file["file_type"] === "pdf" && (
        <Toolbar
          zoomPluginInstance={zoomPluginInstance}
          pageNavigationPluginInstance={pageNavigationPluginInstance}
        />
      )}
      <IconButton onClick={handleDownloadFile}>
        <DownloadRounded sx={{ color: "white" }} />
      </IconButton>
      <Actions
        file={file}
        handleDeleteFile={handleDeleteFile}
        handleUpdateFile={handleUpdateFile}
      />
    </Box>
  );
}

const menu = [
  {
    value: "file_info",
    label: "File Info",
    icon: <InfoRounded />,
  },
  {
    value: "rename_file",
    label: "Rename File",
    icon: <BorderColorRounded />,
  },
  {
    value: "delete_file",
    label: "Delete File",
    icon: <DeleteRounded sx={{ color: "#d32f2f" }} />,
  },
];

export function Actions({ file, handleDeleteFile, handleUpdateFile }) {
  const posthog = usePostHog();
  const theme = useTheme();
  const ref = useRef();
  const [open, setOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isFileDetailsDialogOpen, setIsFileDetailsDialogOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    posthog.capture(CLICK_ACTION_MENU_ON_VIEW_FILE);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleMenuItemClick = (action) => {
    switch (action) {
      case "file_info": {
        setIsFileDetailsDialogOpen(true);
        posthog.capture(VIEW_FILE_INFO);
        break;
      }
      case "rename_file": {
        setIsEditDialogOpen(true);
        break;
      }
      case "delete_file": {
        setIsDeleteDialogOpen(true);
        break;
      }
    }
    handleClose();
  };

  return (
    <>
      <IconButton ref={ref} onClick={handleOpen}>
        <MoreVertRounded sx={{ color: "white" }} />
      </IconButton>
      <Menu open={open} anchorEl={ref.current} onClose={handleClose}>
        {menu.map((menuItem) => {
          const { value, label, icon } = menuItem;
          const isDeleteFile = value === "delete_file";
          return (
            <MenuItem
              key={value}
              sx={{
                color: isDeleteFile ? theme.palette.error.main : "initial",
              }}
              onClick={() => handleMenuItemClick(value)}>
              <ListItemIcon>{icon}</ListItemIcon>
              <ListItemText>{label}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
      {isFileDetailsDialogOpen && (
        <FileDetailsDialog
          file={file}
          onClose={() => setIsFileDetailsDialogOpen(false)}
        />
      )}
      {isDeleteDialogOpen && (
        <ConfirmationDialog
          title={"Delete File"}
          content={`Do you really want to delete this file? This will permanently delete all messages and file.`}
          noLabel={"cancel"}
          yesLabel={"yes, delete"}
          onNo={() => setIsDeleteDialogOpen(false)}
          onYes={() => handleDeleteFile(file["id"])}
          isDelete={true}
        />
      )}
      {isEditDialogOpen && (
        <EditFileDialog
          file={file}
          handleUpdateFile={handleUpdateFile}
          onClose={() => setIsEditDialogOpen(false)}
        />
      )}
    </>
  );
}

export default Header;
